/**
 * ExpandedPlayer - full-screen "now playing" sheet opened by tapping the MiniPlayer.
 * Lets the listener scrub the song, skip next/prev, and jump back/forward 10s.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Play, Pause, SkipForward, SkipBack, RotateCcw, RotateCw } from 'lucide-react';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { formatTime } from '../utils/helpers';

const ExpandedPlayer = ({ open, onClose }) => {
  const {
    currentTrack, isPlaying, togglePlayPause,
    handleNext, handlePrevious, seekTo,
    currentTime, duration,
  } = useAudioPlayer();

  if (!currentTrack) return null;

  const dur = duration || 0;
  const pct = dur > 0 ? (currentTime / dur) * 100 : 0;
  const jump = (secs) => seekTo(Math.max(0, Math.min(dur || currentTime + secs, currentTime + secs)));

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed inset-0 z-[75] bg-[#08060F] flex flex-col"
          data-testid="expanded-player"
        >
          {/* Blurred artwork backdrop */}
          <div className="absolute inset-0 overflow-hidden">
            {currentTrack.artwork && (
              <img src={currentTrack.artwork} alt="" className="w-full h-full object-cover opacity-25 blur-3xl scale-125" />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-[#08060F]/60 via-[#08060F]/85 to-[#08060F]" />
          </div>

          <div className="relative flex flex-col h-full max-w-md mx-auto w-full px-6 pt-4 pb-10">
            {/* Header */}
            <div className="flex items-center justify-between">
              <button onClick={onClose} className="p-2 -ml-2 text-white/80 hover:text-white transition-colors" data-testid="expanded-player-close">
                <ChevronDown className="w-7 h-7" />
              </button>
              <span className="text-white/50 text-xs uppercase tracking-widest">Now Playing</span>
              <span className="w-7" />
            </div>

            {/* Artwork */}
            <div className="flex-1 flex items-center justify-center py-6">
              <div className="w-full max-w-[320px] aspect-square rounded-3xl overflow-hidden shadow-2xl bg-[#1a1625]">
                {currentTrack.artwork ? (
                  <img src={currentTrack.artwork} alt={currentTrack.track_title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F]" />
                )}
              </div>
            </div>

            {/* Track info */}
            <div className="mb-4">
              <h2 className="text-white text-2xl font-bold truncate" data-testid="expanded-player-title">{currentTrack.track_title}</h2>
              <p className="text-[#A1A1AA] text-base truncate">{currentTrack.artist_name}</p>
            </div>

            {/* Seek bar */}
            <div className="mb-6">
              <input
                type="range"
                min={0}
                max={dur || 0}
                step="0.1"
                value={Math.min(currentTime, dur || 0)}
                onChange={(e) => seekTo(parseFloat(e.target.value))}
                className="w-full h-1.5 appearance-none rounded-full cursor-pointer nlt-seek"
                style={{ background: `linear-gradient(to right, #00F0FF ${pct}%, rgba(255,255,255,0.15) ${pct}%)` }}
                data-testid="expanded-player-seek"
              />
              <div className="flex justify-between mt-2 text-xs text-[#71717A] tabular-nums">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(dur)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <button onClick={() => jump(-10)} className="flex flex-col items-center text-white/80 hover:text-white transition-colors" data-testid="expanded-player-rewind10">
                <RotateCcw className="w-6 h-6" />
                <span className="text-[10px] mt-1">10</span>
              </button>
              <button onClick={handlePrevious} className="text-white hover:text-[#00F0FF] transition-colors" data-testid="expanded-player-prev">
                <SkipBack className="w-8 h-8 fill-current" />
              </button>
              <button
                onClick={togglePlayPause}
                className="w-16 h-16 rounded-full bg-[#00F0FF] flex items-center justify-center hover:bg-[#00F0FF]/90 transition-colors shadow-[0_0_30px_rgba(0,240,255,0.4)]"
                data-testid="expanded-player-play"
              >
                {isPlaying ? <Pause className="w-8 h-8 text-[#050505]" /> : <Play className="w-8 h-8 text-[#050505] ml-1" />}
              </button>
              <button onClick={handleNext} className="text-white hover:text-[#00F0FF] transition-colors" data-testid="expanded-player-next">
                <SkipForward className="w-8 h-8 fill-current" />
              </button>
              <button onClick={() => jump(10)} className="flex flex-col items-center text-white/80 hover:text-white transition-colors" data-testid="expanded-player-forward10">
                <RotateCw className="w-6 h-6" />
                <span className="text-[10px] mt-1">10</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ExpandedPlayer;
